import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiChevronLeft, FiMoreHorizontal, FiPlus } from 'react-icons/fi';
import { DashboardSidebar } from '@/layouts/dashboard';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import Swal from 'sweetalert2';
import { useEffect } from 'react';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { t } from 'i18next';
import IconVerified from '@/app/components/Icons/IconVerified';
import { useSession } from '@/hooks/useSession';
import usePermission from '@/hooks/usePermission';
import { ModuleList, ModuleListItem, ModuleListTitle } from '@/components/custom/module';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Button, ButtonBack, ButtonBackContent, ButtonIcon } from '@/components/ui/button';
import { FormField } from '@/components/custom/form';
import { FieldGroup } from '@/components/ui/field';
import { Separator } from '@/components/ui/separator';
import { Filter, FilterTitle } from '@/components/custom/filter';
import { IconWrapper } from '@/components/custom/icon';

export function CourseManageSidebar(props) {
  const navigate = useNavigate();
  const { course, currentModuleId, setCurrentModuleId, loading, setCount, selectedTab } = props;
  const client = useClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredModules, setFilteredModules] = useState([]);
  const [click, setClick] = useState(0);
  const { moduleId } = useParams();

  useEffect(() => {
    setCurrentModuleId(window?.history?.state?.moduleId ?? moduleId);
  }, [click]);

  useEffect(() => {
    if (course && course['learning-systems']) {
      const filtered = course['learning-systems'].filter(item => {
        if (searchTerm === '') {
          return true;
        } else if (item.title.toLowerCase().includes(searchTerm.toLowerCase())) {
          return true;
        }
        return false;
      });
      setFilteredModules(filtered);
    }
  }, [course, searchTerm]);

  const deleteModule = async item => {
    const { error } = await client.delete(`learning_systems/${item.id}`);
    if (error) {
      toast.error('Erro ao deletar módulo');
    } else {
      toast.success('Módulo excluido com sucesso!');
      const updatedModulesList = filteredModules.filter(module => module.id !== item.id);
      setFilteredModules(updatedModulesList);
      setCount(prevState => prevState + 1);
      // window.history.pushState({ moduleId: course?.['learning-systems']?.[0]?.id }, '', `/curso/admin/gerenciar/${course?.id}/${course?.['learning-systems']?.[0]?.id}`);
      // setClick(prevState => prevState + 1);
    }
  };

  const shortByCreation = (a, b) => {
    const dateA = new Date(a?.['updated_at']);
    const dateB = new Date(b?.['updated_at']);
    return dateB - dateA;
  };

  filteredModules?.sort(shortByCreation);

  const handleDelete = item => {
    Swal.fire({
      title: t('projects.warningDeleteModule'),
      text: t('projects.warningDeleteCannotBeUndoneText'),
      confirmButtonText: t('button.yes'),
      showDenyButton: true,
      denyButtonText: t('button.no'),
      showCloseButton: true
    }).then(res => {
      if (res.isConfirmed) {
        deleteModule(item);
      }
    });
  };

  const currentUser = useSession()?.session?.user;
  const hasManagePermission = usePermission('Gerenciar conteúdo', 'Cursos');

  const hasModulePermission = module => {
    if (hasManagePermission && (!module?.['authored_by_user'] || (module?.['authored_by_user'] && currentUser.id === module?.['creator_id']) || currentUser?.profileNames.includes('Administrador'))) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <DashboardSidebar
      hasBrand={false}
      viewMode="create"
    >
      <Filter>
        <ButtonBack onClick={() => navigate('/curso/admin')}>
          <IconWrapper>
            <FiChevronLeft />
          </IconWrapper>
          <ButtonBackContent title={course?.title}>
            <FilterTitle>{course?.title}</FilterTitle>
          </ButtonBackContent>
        </ButtonBack>
      </Filter>

      <FieldGroup>
        <FormField
          label={t('textsCommon.modules')}
          placeholder={t('textsCommon.research')}
          type="search"
          onChange={e => setSearchTerm(e.target.value)}
        />

        {course?.id ? (
          <Button
            variant="outline-dashed"
            fullWidth
            size="sm"
            asChild
          >
            <Link to={`/lms/criar/modulo/nome/${course?.id}`}>
              <ButtonIcon>
                <FiPlus />
              </ButtonIcon>
              {t('button.newModule')}
            </Link>
          </Button>
        ) : (
          <Loader />
        )}
      </FieldGroup>

      <Separator className="my-8" />

      <ModuleList>
        {loading && <Loader />}

        {!loading && filteredModules.length === 0 && <EmptyState type="data" />}

        {!loading &&
          filteredModules?.map(item => (
            <ModuleListItem
              key={item.id}
              onClick={() => {
                if (hasModulePermission(item)) {
                  setClick(prevState => prevState + 1);
                  navigate(`/curso/admin/gerenciar/${course?.id}/${item.id}/${selectedTab}/d`);
                }
              }}
              active={currentModuleId == item.id}
              style={!hasModulePermission(item) ? { cursor: 'default' } : {}}
            >
              <ModuleListTitle>
                {item?.official_content && <IconVerified />} {item.title}
              </ModuleListTitle>

              <div onClick={e => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button">
                      <FiMoreHorizontal size={16} />
                    </button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    align="end"
                    style={{ zIndex: 9999 }}
                  >
                    <DropdownMenuItem onSelect={() => navigate(`/show-course/${item.id}/classroom`)}>{t('button.view')}</DropdownMenuItem>

                    {hasModulePermission(item) && (
                      <>
                        <DropdownMenuItem onSelect={() => navigate(`/lms/criar/modulo/nome/${item.id}/${course?.id}`)}>{t('button.edit')}</DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-danger"
                          onSelect={() => handleDelete(item)}
                        >
                          {t('button.delete')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </ModuleListItem>
          ))}
      </ModuleList>
    </DashboardSidebar>
  );
}
